import { useState } from 'react'
import { IconSparkles, IconX, IconAlertTriangle } from '@tabler/icons-react'

export type AiSuggestions = {
  title: string | null
  brand: string | null
  description: string | null
  condition: string | null
  activityLabels: string[]
  colorLabels: string[]
  roleLabels: string[]
  sceneLabels: string[]
  confidence: 'low' | 'medium' | 'high'
}

type OptionWithId = { id: string; value: string; label: string }

type ResolvedSuggestions = {
  title: string | null
  brand: OptionWithId | null
  brandRaw: string | null
  description: string | null
  condition: OptionWithId | null
  activityIds: string[]
  activityUnmatched: string[]
  colorIds: string[]
  colorUnmatched: string[]
  roleIds: string[]
  roleUnmatched: string[]
  sceneIds: string[]
  sceneUnmatched: string[]
  confidence: 'low' | 'medium' | 'high'
}

export type ApplyField =
  | 'title'
  | 'brand'
  | 'description'
  | 'condition'
  | 'activities'
  | 'colors'
  | 'roles'
  | 'scenes'

type Props = {
  suggestions: AiSuggestions
  opts: {
    conditions: OptionWithId[]
    brands: OptionWithId[]
    activities: OptionWithId[]
    colors: OptionWithId[]
    roles: OptionWithId[]
    scenes: OptionWithId[]
  }
  onApplyField: (field: ApplyField, value: unknown) => void
  onClose: () => void
}

function norm(s: string): string {
  return s.toLowerCase().replace(/[-_]/g, ' ').replace(/\s+/g, ' ').trim()
}

const SYNONYM_GROUPS: string[][] = [
  ['gray', 'grey'],
  ['neon yellow', 'hi-vis yellow', 'fluorescent yellow', 'yellow', 'acid yellow', 'lime yellow', 'electric yellow'],
  ['neon green', 'fluorescent green', 'lime green', 'hi-vis green'],
  ['neon orange', 'fluorescent orange', 'hi-vis orange'],
  ['sports', 'sportswear', 'athletic', 'sport', 'athletics'],
  ['athletic wear', 'sportswear', 'sports'],
  ['navy', 'navy blue', 'dark blue'],
  ['khaki', 'tan', 'beige'],
  ['maroon', 'dark red', 'burgundy', 'wine'],
  ['pup play', 'puppy play', 'pup'],
  ['bdsm', 'fetish', 'kink'],
  ['protective', 'protective gear', 'armor', 'armour'],
  ['footwear', 'boots', 'shoes'],
  ['restraint', 'bondage', 'restraints'],
  ['abdl', 'adult baby', 'diaper'],
  ['rubber', 'latex', 'pvc'],
]

function getSynonyms(label: string): string[] {
  const n = norm(label)
  const group = SYNONYM_GROUPS.find(g => g.some(s => norm(s) === n))
  return group ? group : [n]
}

function matchByLabel(labels: string[], options: OptionWithId[]): { matched: OptionWithId[]; unmatched: string[] } {
  const matched: OptionWithId[] = []
  const unmatched: string[] = []
  const usedIds = new Set<string>()

  for (const label of labels) {
    const synonyms = getSynonyms(label).map(s => norm(s))

    let found = options.find(o =>
      !usedIds.has(o.id) &&
      (synonyms.includes(norm(o.label)) || synonyms.includes(norm(o.value)))
    )

    if (!found) {
      found = options.find(o => {
        if (usedIds.has(o.id)) return false
        const ol = norm(o.label)
        const ov = norm(o.value)
        return synonyms.some(s =>
          ol.includes(s) || s.includes(ol) ||
          ov.includes(s) || s.includes(ov)
        )
      })
    }

    if (found) {
      matched.push(found)
      usedIds.add(found.id)
    } else {
      unmatched.push(label)
    }
  }
  return { matched, unmatched }
}

function matchCondition(condValue: string | null, options: OptionWithId[]): OptionWithId | null {
  if (!condValue) return null
  const synonyms = getSynonyms(condValue).map(s => norm(s))
  return options.find(o =>
    synonyms.includes(norm(o.value)) || synonyms.includes(norm(o.label))
  ) ?? null
}

function resolveAll(suggestions: AiSuggestions, opts: Props['opts']): ResolvedSuggestions {
  const brandMatch = suggestions.brand
    ? opts.brands.find(b => b.label.toLowerCase() === suggestions.brand!.toLowerCase()) ?? null
    : null

  const { matched: activityMatched, unmatched: activityUnmatched } = matchByLabel(suggestions.activityLabels, opts.activities)
  const { matched: colorMatched, unmatched: colorUnmatched } = matchByLabel(suggestions.colorLabels, opts.colors)
  const { matched: roleMatched, unmatched: roleUnmatched } = matchByLabel(suggestions.roleLabels, opts.roles)
  const { matched: sceneMatched, unmatched: sceneUnmatched } = matchByLabel(suggestions.sceneLabels, opts.scenes)

  return {
    title: suggestions.title,
    brand: brandMatch,
    brandRaw: suggestions.brand,
    description: suggestions.description,
    condition: matchCondition(suggestions.condition, opts.conditions),
    activityIds: activityMatched.map(o => o.id),
    activityUnmatched,
    colorIds: colorMatched.map(o => o.id),
    colorUnmatched,
    roleIds: roleMatched.map(o => o.id),
    roleUnmatched,
    sceneIds: sceneMatched.map(o => o.id),
    sceneUnmatched,
    confidence: suggestions.confidence,
  }
}

const CONFIDENCE_STYLE: Record<string, string> = {
  high:   'text-green-400',
  medium: 'text-amber-400',
  low:    'text-red-400',
}

function Chip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border border-zinc-600 text-zinc-300 bg-surface-raised">
      {label}
    </span>
  )
}

function UnmatchedChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border border-dashed border-zinc-700 text-zinc-500"
      title="No matching option in your catalog">
      <IconAlertTriangle size={9} className="text-amber-600" /> {label}
    </span>
  )
}

function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!on)}
      disabled={disabled}
      className={[
        'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors duration-200',
        disabled ? 'opacity-30 cursor-not-allowed bg-zinc-700'
          : on ? 'bg-brand-orange cursor-pointer'
          : 'bg-zinc-700 cursor-pointer',
      ].join(' ')}>
      <span className={[
        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform duration-200',
        on ? 'translate-x-4' : 'translate-x-0',
      ].join(' ')} />
    </button>
  )
}

type RowProps = {
  label: string
  hasValue: boolean
  canApply: boolean   // false = nothing to apply (unmatched brand etc.) — toggle disabled
  enabled: boolean
  onToggle: (v: boolean) => void
  children: React.ReactNode
}

function SuggestionRow({ label, hasValue, canApply, enabled, onToggle, children }: RowProps) {
  if (!hasValue) return null
  return (
    <div className={[
      'flex items-start gap-3 py-3 border-b border-border-subtle last:border-0 transition-opacity',
      !canApply || !enabled ? 'opacity-50' : '',
    ].join(' ')}>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-medium uppercase tracking-widest text-zinc-500 mb-1.5">{label}</p>
        <div className="flex flex-wrap gap-1.5">{children}</div>
      </div>
      <Toggle on={enabled} onChange={onToggle} disabled={!canApply} />
    </div>
  )
}

export function AiSuggestionsPanel({ suggestions, opts, onApplyField, onClose }: Props) {
  const resolved = resolveAll(suggestions, opts)

  // All applicable fields start toggled ON; user can opt fields out
  const initialDisabled = new Set<ApplyField>()
  const [disabled, setDisabled] = useState<Set<ApplyField>>(initialDisabled)

  const toggle = (field: ApplyField, on: boolean) => {
    setDisabled(prev => {
      const next = new Set(prev)
      if (on) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const isOn = (field: ApplyField) => !disabled.has(field)

  // Fields that have something to apply (matched values exist)
  const applicableFields: Array<[ApplyField, unknown]> = [
    ...(resolved.title ? [['title', resolved.title] as [ApplyField, unknown]] : []),
    ...(resolved.brand ? [['brand', resolved.brand.id] as [ApplyField, unknown]] : []),
    ...(resolved.description ? [['description', resolved.description] as [ApplyField, unknown]] : []),
    ...(resolved.condition ? [['condition', resolved.condition.id] as [ApplyField, unknown]] : []),
    ...(resolved.activityIds.length ? [['activities', resolved.activityIds] as [ApplyField, unknown]] : []),
    ...(resolved.colorIds.length ? [['colors', resolved.colorIds] as [ApplyField, unknown]] : []),
    ...(resolved.roleIds.length ? [['roles', resolved.roleIds] as [ApplyField, unknown]] : []),
    ...(resolved.sceneIds.length ? [['scenes', resolved.sceneIds] as [ApplyField, unknown]] : []),
  ]

  const handleApplyAndClose = () => {
    for (const [field, value] of applicableFields) {
      if (!disabled.has(field)) {
        onApplyField(field, value)
      }
    }
    onClose()
  }

  const anyWillApply = applicableFields.some(([field]) => !disabled.has(field))

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-surface-overlay border border-border-default rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-border-subtle shrink-0">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-orange/15 flex items-center justify-center">
              <IconSparkles size={16} className="text-brand-orange" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-zinc-100">AI Identification</h3>
              <p className={`text-xs ${CONFIDENCE_STYLE[resolved.confidence]}`}>
                {resolved.confidence.charAt(0).toUpperCase() + resolved.confidence.slice(1)} confidence
              </p>
            </div>
          </div>
          <button type="button" onClick={onClose}
            className="p-1.5 rounded-lg text-zinc-500 hover:text-zinc-300 hover:bg-surface-raised transition-colors">
            <IconX size={16} />
          </button>
        </div>

        {/* Instructions */}
        <div className="px-5 pt-3 pb-1 shrink-0">
          <p className="text-[11px] text-zinc-500">
            Toggle off any fields you don't want applied, then tap <span className="text-zinc-400 font-medium">Apply & Close</span>.
          </p>
        </div>

        {/* Suggestions list */}
        <div className="flex-1 overflow-y-auto px-5 py-1">
          <SuggestionRow
            label="Title" hasValue={!!resolved.title} canApply={!!resolved.title}
            enabled={isOn('title')} onToggle={v => toggle('title', v)}>
            <span className="text-sm text-zinc-200">{resolved.title}</span>
          </SuggestionRow>

          <SuggestionRow
            label="Brand" hasValue={!!(resolved.brand || resolved.brandRaw)} canApply={!!resolved.brand}
            enabled={isOn('brand')} onToggle={v => toggle('brand', v)}>
            {resolved.brand
              ? <Chip label={resolved.brand.label} />
              : <span className="text-xs text-zinc-500 italic">{resolved.brandRaw} <span className="text-amber-600">(not in catalog)</span></span>}
          </SuggestionRow>

          <SuggestionRow
            label="Description" hasValue={!!resolved.description} canApply={!!resolved.description}
            enabled={isOn('description')} onToggle={v => toggle('description', v)}>
            <span className="text-xs text-zinc-300 leading-relaxed">{resolved.description}</span>
          </SuggestionRow>

          <SuggestionRow
            label="Condition" hasValue={!!resolved.condition} canApply={!!resolved.condition}
            enabled={isOn('condition')} onToggle={v => toggle('condition', v)}>
            {resolved.condition && <Chip label={resolved.condition.label} />}
          </SuggestionRow>

          <SuggestionRow
            label="Activity"
            hasValue={resolved.activityIds.length > 0 || resolved.activityUnmatched.length > 0}
            canApply={resolved.activityIds.length > 0}
            enabled={isOn('activities')} onToggle={v => toggle('activities', v)}>
            {opts.activities
              .filter(o => resolved.activityIds.includes(o.id))
              .map(o => <Chip key={o.id} label={o.label} />)}
            {resolved.activityUnmatched.map(l => <UnmatchedChip key={l} label={l} />)}
          </SuggestionRow>

          <SuggestionRow
            label="Colors"
            hasValue={resolved.colorIds.length > 0 || resolved.colorUnmatched.length > 0}
            canApply={resolved.colorIds.length > 0}
            enabled={isOn('colors')} onToggle={v => toggle('colors', v)}>
            {opts.colors
              .filter(o => resolved.colorIds.includes(o.id))
              .map(o => <Chip key={o.id} label={o.label} />)}
            {resolved.colorUnmatched.map(l => <UnmatchedChip key={l} label={l} />)}
          </SuggestionRow>

          <SuggestionRow
            label="Role"
            hasValue={resolved.roleIds.length > 0 || resolved.roleUnmatched.length > 0}
            canApply={resolved.roleIds.length > 0}
            enabled={isOn('roles')} onToggle={v => toggle('roles', v)}>
            {opts.roles
              .filter(o => resolved.roleIds.includes(o.id))
              .map(o => <Chip key={o.id} label={o.label} />)}
            {resolved.roleUnmatched.map(l => <UnmatchedChip key={l} label={l} />)}
          </SuggestionRow>

          <SuggestionRow
            label="Scene"
            hasValue={resolved.sceneIds.length > 0 || resolved.sceneUnmatched.length > 0}
            canApply={resolved.sceneIds.length > 0}
            enabled={isOn('scenes')} onToggle={v => toggle('scenes', v)}>
            {opts.scenes
              .filter(o => resolved.sceneIds.includes(o.id))
              .map(o => <Chip key={o.id} label={o.label} />)}
            {resolved.sceneUnmatched.map(l => <UnmatchedChip key={l} label={l} />)}
          </SuggestionRow>

          <p className="text-[10px] text-zinc-600 italic text-center pt-3 pb-1">
            AI suggestions — please review before applying
          </p>
        </div>

        {/* Footer */}
        <div className="flex gap-2 px-5 py-4 border-t border-border-subtle shrink-0">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 border border-border-default hover:bg-surface-raised transition-colors">
            Cancel
          </button>
          <button type="button" onClick={handleApplyAndClose} disabled={!anyWillApply}
            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium bg-brand-orange text-white hover:bg-brand-orange-dark disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
            Apply & Close
          </button>
        </div>
      </div>
    </div>
  )
}
